import { mapApiFieldErrors, showApiError } from '@/errors';
import { isAddressEmpty } from '@/utils/address';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SlideOver, SlideOverFooter } from '../../../components/Shared/SlideOver';
import { Input } from '../../../components/Shared/Input';
import { FormField } from '../../../components/Shared/FormField';
import { PasswordCriteria } from '../../../components/Shared/PasswordCriteria';
import { isPasswordValid } from '../../../utils/password';
import type { FacilityFlowCoreModulesEmployeesDtosEmployeeDto } from '../../../api/generated/models';
import { formFieldInputClassName } from '../../../components/Shared/formFieldStyles';
import { useTimeClockGroupStore } from '../../../store/useTimeClockGroupStore';

interface EmployeeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EmployeeFormData) => Promise<void>;
  employee?: FacilityFlowCoreModulesEmployeesDtosEmployeeDto | null;
  mode: 'create' | 'edit';
}

export interface EmployeeFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  birthDate?: string;
  hireDate?: string;
  password?: string;
  personalNumber?: string;
  socialSecurityNumber?: string;
  iban?: string;
  bic?: string;
  address?: {
    street?: string;
    streetNumber?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  /** The app asks "Homeoffice oder Büro?" at clock-in. */
  allowsHomeOffice: boolean;
  /** Time clock group (FACF-149); '' = the tenant's default clock-in window. */
  timeClockGroupId: string;
}

type FormErrors = Record<string, string>;

const REQUIRED_ADDRESS_PARTS = ['street', 'postalCode', 'city', 'country'] as const;

const FORM_FIELDS = [
  'firstName', 'lastName', 'email', 'phone', 'role', 'birthDate', 'hireDate', 'password',
  'personalNumber', 'socialSecurityNumber', 'iban', 'bic',
  'address.street', 'address.streetNumber', 'address.city', 'address.postalCode', 'address.country',
];

/** Maps an API field path ("Address.City", "IBAN") to the form's field key. */
function toFormField(apiField: string): string {
  const normalized = apiField.toLowerCase();
  return FORM_FIELDS.find((field) => field.toLowerCase() === normalized) ?? apiField;
}

export function EmployeeDialog({
  isOpen,
  onClose,
  onSave,
  employee,
  mode,
}: EmployeeDialogProps) {
  const { t } = useTranslation(['employees', 'common']);
  const [formData, setFormData] = useState<EmployeeFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'Mitarbeiter',
    birthDate: '',
    hireDate: '',
    password: '',
    socialSecurityNumber: '',
    iban: '',
    bic: '',
    address: {
      street: '',
      streetNumber: '',
      city: '',
      postalCode: '',
      country: '',
    },
    allowsHomeOffice: false,
    timeClockGroupId: '',
  });

  const timeClockGroups = useTimeClockGroupStore((s) => s.groups);
  const fetchTimeClockGroups = useTimeClockGroupStore((s) => s.fetchGroups);

  useEffect(() => {
    if (isOpen) void fetchTimeClockGroups();
  }, [isOpen, fetchTimeClockGroups]);

  // Only active groups can be picked; the assigned one stays visible even when inactive.
  const groupOptions = useMemo(
    () =>
      timeClockGroups.filter(
        (g) => g.isActive || g.timeClockGroupId === formData.timeClockGroupId,
      ),
    [timeClockGroups, formData.timeClockGroupId],
  );
  const assignedGroupUnknown =
    formData.timeClockGroupId !== '' &&
    !timeClockGroups.some((g) => g.timeClockGroupId === formData.timeClockGroupId);

  // Keys are form field names; address parts use "address.street" etc.
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && employee) {
      setFormData({
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        email: employee.email || '',
        phone: employee.phone || '',
        role: employee.role || 'Mitarbeiter',
        birthDate: employee.birthDate ? new Date(employee.birthDate).toISOString().split('T')[0] : '',
        hireDate: employee.hireDate ? employee.hireDate.slice(0, 10) : '',
        personalNumber: employee.personalNumber || '',
        password: '',
        socialSecurityNumber: employee.socialSecurityNumber || '',
        iban: employee.iban || '',
        bic: employee.bic || '',
        address: {
          street: employee.address?.street || '',
          streetNumber: employee.address?.streetNumber || '',
          city: employee.address?.city || '',
          postalCode: employee.address?.postalCode || '',
          country: employee.address?.country || '',
        },
        allowsHomeOffice: employee.allowsHomeOffice ?? false,
        timeClockGroupId: employee.timeClockGroupId ?? '',
      });
    } else if (mode === 'create') {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'Mitarbeiter',
        birthDate: '',
        hireDate: '',
        password: '',
        socialSecurityNumber: '',
        iban: '',
        bic: '',
        address: {
          street: '',
          streetNumber: '',
          city: '',
          postalCode: '',
          country: '',
        },
        allowsHomeOffice: false,
        timeClockGroupId: '',
      });
    }
    setErrors({});
  }, [mode, employee, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('employees:validation.firstNameRequired');
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('employees:validation.lastNameRequired');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('employees:validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('employees:validation.emailInvalid');
    }
    if (!formData.hireDate) {
      newErrors.hireDate = t('employees:validation.hireDateRequired');
    }
    if (mode === 'create' && !formData.password) {
      newErrors.password = t('employees:validation.passwordRequired');
    }
    if (mode === 'create' && formData.password && !isPasswordValid(formData.password)) {
      newErrors.password = t('validation:password.invalid');
    }
    if (mode === 'create' && formData.personalNumber && formData.personalNumber.length < 4) {
        newErrors.personalNumber = t('employees:validation.personalNumberMinLength');
    }

    // The address is optional, but if any part is filled in, it has to be complete.
    if (!isAddressEmpty(formData.address)) {
      for (const part of REQUIRED_ADDRESS_PARTS) {
        if (!formData.address?.[part]?.trim()) {
          newErrors[`address.${part}`] = t('employees:validation.addressIncomplete');
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error: unknown) {
      console.error('Error saving employee:', error);

      // Field errors (e.g. "address.City") are shown at the field; anything else as a toast.
      const fieldErrors = mapApiFieldErrors(error);
      const backendErrors: FormErrors = {};
      for (const [apiField, message] of Object.entries(fieldErrors)) {
        backendErrors[toFormField(apiField)] = message;
      }
      if (Object.keys(backendErrors).length > 0) {
        setErrors(backendErrors);
      } else {
        showApiError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof EmployeeFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? t('employees:dialog.titleCreate') : t('employees:dialog.titleEdit')}
      size="lg"
      footer={
        <SlideOverFooter
          onCancel={onClose}
          onConfirm={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
          confirmText={mode === 'create' ? t('employees:dialog.buttonCreate') : t('employees:dialog.buttonSave')}
          confirmDisabled={isSubmitting}
          loading={isSubmitting}
        />
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* PersonalNumber */}
          <FormField label={t('employees:fields.personalNumber')} required error={errors.personalNumber}>
            <Input
                value={formData.personalNumber}
                onChange={(e) => handleChange('personalNumber', e.target.value)}
                placeholder={t('employees:placeholders.personalNumber')}
                disabled={isSubmitting}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Vorname */}
          <FormField label={t('employees:fields.firstName')} required error={errors.firstName}>
            <Input
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              placeholder={t('employees:placeholders.firstName')}
              disabled={isSubmitting}
            />
          </FormField>

          {/* Nachname */}
          <FormField label={t('employees:fields.lastName')} required error={errors.lastName}>
            <Input
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              placeholder={t('employees:placeholders.lastName')}
              disabled={isSubmitting}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* E-Mail */}
          <FormField label={t('employees:fields.email')} required error={errors.email}>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder={t('employees:placeholders.email')}
              disabled={isSubmitting}
            />
          </FormField>

          {/* Telefon */}
          <FormField label={t('employees:fields.phone')} error={errors.phone}>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder={t('employees:placeholders.phone')}
              disabled={isSubmitting}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Rolle */}
          <FormField label={t('employees:fields.role')} required>
            <select
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              disabled={isSubmitting}
              aria-label={t('employees:dialog.selectRole')}
            >
              <option value="Mitarbeiter">{t('employees:roles.employee')}</option>
              <option value="Verwaltung">{t('employees:roles.administration')}</option>
            </select>
          </FormField>

          {/* Geburtsdatum */}
          <FormField label={t('employees:fields.birthDate')} error={errors.birthDate}>
            <Input
              type="date"
              value={formData.birthDate}
              onChange={(e) => handleChange('birthDate', e.target.value)}
              disabled={isSubmitting}
            />
          </FormField>
        </div>

        {/* Eintrittsdatum */}
        <FormField label={t('employees:fields.hireDate')} required error={errors.hireDate}>
          <Input
            type="date"
            value={formData.hireDate}
            onChange={(e) => handleChange('hireDate', e.target.value)}
            disabled={isSubmitting}
          />
        </FormField>

        {/* Homeoffice */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="allowsHomeOffice"
            checked={formData.allowsHomeOffice}
            onChange={(e) => setFormData((prev) => ({ ...prev, allowsHomeOffice: e.target.checked }))}
            disabled={isSubmitting}
            className="w-4 h-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30"
          />
          <label htmlFor="allowsHomeOffice" className="text-[13px] text-slate-700">
            {t('employees:fields.allowsHomeOffice')}
          </label>
        </div>

        {/* Stempelgruppe (FACF-149) */}
        <FormField
          label={t('employees:fields.timeClockGroup')}
          helperText={t('employees:hints.timeClockGroup')}
        >
          <select
            value={formData.timeClockGroupId}
            onChange={(e) => setFormData((prev) => ({ ...prev, timeClockGroupId: e.target.value }))}
            className={formFieldInputClassName(false)}
            disabled={isSubmitting}
            aria-label={t('employees:fields.timeClockGroup')}
          >
            <option value="">{t('employees:fields.timeClockGroupDefault')}</option>
            {groupOptions.map((group) => (
              <option key={group.timeClockGroupId} value={group.timeClockGroupId}>
                {group.isActive
                  ? group.name
                  : t('employees:fields.timeClockGroupInactive', { name: group.name })}
              </option>
            ))}
            {assignedGroupUnknown && (
              <option value={formData.timeClockGroupId}>
                {t('employees:fields.timeClockGroupUnknown')}
              </option>
            )}
          </select>
        </FormField>

        {/* Divider */}
        <div className="border-t border-gray-200 dark:border-gray-700 my-6"></div>

        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('employees:dialog.sectionAdditionalDetails')}
        </h3>

        {/* SV-Nummer */}
        <FormField label={t('employees:fields.socialSecurityNumber')} error={errors.socialSecurityNumber}>
          <Input
            value={formData.socialSecurityNumber}
            onChange={(e) => handleChange('socialSecurityNumber', e.target.value)}
            placeholder={t('employees:placeholders.socialSecurityNumber')}
            disabled={isSubmitting}
          />
        </FormField>

        {/* Bankverbindung */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t('employees:dialog.sectionBankDetails')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={t('employees:fields.iban')} error={errors.iban}>
              <Input
                value={formData.iban}
                onChange={(e) => handleChange('iban', e.target.value)}
                placeholder={t('employees:placeholders.iban')}
                disabled={isSubmitting}
              />
            </FormField>
            <FormField label={t('employees:fields.bic')} error={errors.bic}>
              <Input
                value={formData.bic}
                onChange={(e) => handleChange('bic', e.target.value)}
                placeholder={t('employees:placeholders.bic')}
                disabled={isSubmitting}
              />
            </FormField>
          </div>
        </div>

        {/* Wohnadresse */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t('employees:dialog.sectionAddress')}
          </h4>
          <p className="-mt-2 mb-3 text-xs text-gray-500 dark:text-gray-400">
            {t('employees:dialog.addressOptional')}
          </p>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <FormField label={t('employees:fields.street')} error={errors['address.street']}>
                  <Input
                    value={formData.address?.street || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      address: { ...prev.address, street: e.target.value }
                    }))}
                    placeholder={t('employees:placeholders.street')}
                    disabled={isSubmitting}
                  />
                </FormField>
              </div>
              <div>
                <FormField label={t('employees:fields.streetNumber')} error={errors['address.streetNumber']}>
                  <Input
                    value={formData.address?.streetNumber || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      address: { ...prev.address, streetNumber: e.target.value }
                    }))}
                    placeholder={t('employees:placeholders.streetNumber')}
                    disabled={isSubmitting}
                  />
                </FormField>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label={t('employees:fields.postalCode')} error={errors['address.postalCode']}>
                <Input
                  value={formData.address?.postalCode || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, postalCode: e.target.value }
                  }))}
                  placeholder={t('employees:placeholders.postalCode')}
                  disabled={isSubmitting}
                />
              </FormField>
              <FormField label={t('employees:fields.city')} error={errors['address.city']}>
                <Input
                  value={formData.address?.city || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, city: e.target.value }
                  }))}
                  placeholder={t('employees:placeholders.city')}
                  disabled={isSubmitting}
                />
              </FormField>
            </div>
            <FormField label={t('employees:fields.country')} error={errors['address.country']}>
              <Input
                value={formData.address?.country || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  address: { ...prev.address, country: e.target.value }
                }))}
                placeholder={t('employees:placeholders.country')}
                disabled={isSubmitting}
              />
            </FormField>
          </div>
        </div>

        {/* Passwort (nur bei Create) */}
        {mode === 'create' && (
          <FormField label={t('employees:fields.password')} required error={errors.password}>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              placeholder={t('employees:placeholders.password')}
              disabled={isSubmitting}
            />
            <PasswordCriteria password={formData.password ?? ''} />
          </FormField>
        )}
      </form>
    </SlideOver>
  );
}
